import React, { useCallback, useEffect, useState } from 'react';
import { useSlackAuth } from 'features/auth/SlackAuthContext.tsx';
import { useLoadingLabel } from 'shared/hooks/useGracePeriodVisible';
import { talosBadgesFaviconURL } from 'config/constants';
import { ensureAppFonts } from 'shared/lib/display-fonts';
import { LoginBadgeBackdrop } from 'shared/components/LoginBadgeBackdrop';
import './SlackLoginScreen.css';

export const RULES_SETUP_URL =
  'https://talostrading.atlassian.net/wiki/spaces/TALOS/pages/3761405955/Talos+Badges+Rules+Setup';

interface SlackLoginScreenProps {
  ready?: boolean;
  loadingLabel?: string;
}

export function SlackLoginScreen({ ready = true, loadingLabel = 'Loading Badges' }: SlackLoginScreenProps) {
  const { login } = useSlackAuth();
  const [fieldReady, setFieldReady] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  // Sticky — zoom/resize must not hide the login card after the field first finishes.
  const onFieldReady = useCallback((readyField: boolean) => {
    if (readyField) setFieldReady(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    ensureAppFonts().then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Login card waits until the field has finished animating in and typefaces are ready.
  const contentReady = ready && fieldReady && fontsReady;
  const { visible: showLoadingLabel } = useLoadingLabel(loadingLabel, !contentReady);

  return (
    <div className="slack-login-screen">
      {/* Color pops start 200ms after enableFlash — only once the login box is up. */}
      <LoginBadgeBackdrop enableFlash={contentReady && !showLoadingLabel} onFieldReady={onFieldReady} />
      {contentReady && !showLoadingLabel ? (
        <div className="slack-login-box">
          <img src={talosBadgesFaviconURL} alt="Talos Badges" width="38" height="38" className="spinning-slower" />
          <h1>Talos Badges</h1>
          <p>Sign in with your Talos Slack account</p>
          <button className="slack-login-btn" onClick={login}>
            <svg width="20" height="20" viewBox="0 0 127 127" fill="none">
              <path
                d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z"
                fill="#E01E5A"
              />
              <path
                d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z"
                fill="#36C5F0"
              />
              <path
                d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z"
                fill="#2EB67D"
              />
              <path
                d="M80.1 99.9c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.9h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z"
                fill="#ECB22E"
              />
            </svg>
            Sign in with Slack
          </button>
          <div>
            <a href={RULES_SETUP_URL} target="_blank" rel="noopener noreferrer" className="rules-link">
              View Rules & Setup
            </a>
          </div>
        </div>
      ) : showLoadingLabel ? (
        <div className="slack-login-loading" role="status" aria-live="polite">
          <p>{loadingLabel}</p>
          <img src={talosBadgesFaviconURL} alt="" className="spinning" width="20" height="20" />
        </div>
      ) : null}
    </div>
  );
}
